/**
 * PD Naja utils & extensions - Utilities and extensions created for use with Naja library. 
 * https://github.com/peckadesign/pd-naja#readme
 *
 * @author PeckaDesign, s.r.o <support@peckadesign.cz>
 * @license MIT
 *
 * @version 1.2.0
 */

import naja from 'naja';

let instance = null;
class ControlManager {
    constructor() {
        this.onLoadControl = [];
        this.onLiveControl = [];
        if (instance === null) {
            // eslint-disable-next-line @typescript-eslint/no-this-alias
            instance = this;
            naja.snippetHandler.addEventListener('beforeUpdate', this.onBeforeSnippetUpdate.bind(this));
            naja.snippetHandler.addEventListener('afterUpdate', this.onSnippetUpdate.bind(this));
        }
        return instance;
    }
    onLoad() {
        this.initialize(this.onLoadControl);
        this.initialize(this.onLiveControl);
    }
    onBeforeSnippetUpdate(event) {
        if (event.detail.operation === naja.snippetHandler.op.append ||
            event.detail.operation === naja.snippetHandler.op.prepend) {
            return;
        }
        this.destroy(this.onLiveControl, event.detail.snippet);
    }
    onSnippetUpdate(event) {
        this.initialize(this.onLiveControl, event.detail.snippet);
    }
    initialize(controls, snippet) {
        controls.forEach((control) => {
            control.initialize(snippet || document);
        });
    }
    destroy(controls, snippet) {
        controls.forEach((control) => {
            if (control.destroy !== undefined) {
                control.destroy(snippet);
            }
        });
    }
    addControlOnLoad(control) {
        this.onLoadControl.push(control);
    }
    addControlOnLive(control) {
        this.onLiveControl.push(control);
    }
}

class AjaxModalExtension {
    constructor(modal) {
        this.uniqueExtKey = 'modal';
        this.popstateFlag = false;
        this.hidePopstateFlag = false;
        this.historyEnabled = false; // (dis)allows `pushState` after hiding the modal when going back in history (popstate), we don't want to push new state into history same is true also when the history is disabled for request altogether
        this.historyDirection = 'backwards';
        this.shouldPreventSnippetFetch = false;
        this.modalOptions = {};
        this.original = []; // stack of states under the modal after hiding the modal with `forwards` history mode, we need to push the previous state
        this.lastState = null;
        this.abortControllers = new Map();
        this.isRequestWithHistory = (options) => {
            return options.history !== false;
        };
        this.isPdModalRequest = (options) => {
            return Boolean(options.pdModal);
        };
        this.isPdModalState = (state) => {
            return 'pdModal' in state;
        };
        this.restoreExtensionPropertiesFromState = (state) => {
            this.historyEnabled = true; // Called from popstateHandler means the history is enabled
            this.historyDirection = state.pdModal.historyDirection;
            this.modalOptions = state.pdModal.options;
        };
        // Extension popstate has to be executed before naja popstate, so we can correctly detect if the pdModal is
        // opened. Therefore, we bind the callback before the extension initialization itself.
        window.addEventListener('popstate', this.popstateHandler.bind(this));
        this.modal = modal;
        this.initialState = history.state || {};
    }
    initialize(naja) {
        naja.uiHandler.addEventListener('interaction', this.checkExtensionEnabled.bind(this));
        naja.historyHandler.addEventListener('buildState', this.buildState.bind(this));
        naja.snippetCache.addEventListener('fetch', this.onSnippetFetch.bind(this));
        naja.addEventListener('before', this.before.bind(this));
        naja.addEventListener('start', this.abortPreviousRequest.bind(this));
        naja.addEventListener('success', this.success.bind(this));
        naja.addEventListener('complete', this.clearRequest.bind(this));
        this.modal.onShow(this.showHandler.bind(this));
        this.modal.onHide(() => {
            this.removeModalSnippetsIds();
            this.abortControllers.get('modal')?.abort();
        });
        this.modal.onHidden(this.hiddenHandler.bind(this));
    }
    checkExtensionEnabled(event) {
        const { element, options } = event.detail;
        options.pdModal =
            this.modal.isShown() ||
                element.hasAttribute('data-naja-modal') ||
                element.form?.hasAttribute('data-naja-modal');
        if (!this.isPdModalRequest(options)) {
            return;
        }
        // `modalOptions` will be stored in state, therefore no `Element` is allowed. These options are also forwarded
        // to other Naja event handlers via `options`. We also store the element separately to be used there as well.
        this.modalOptions = this.modal.getOptions(element);
        options.modalOptions = this.modalOptions;
        options.modalOpener = element;
        // If the extension is enabled and modal is not opened, we detect and store history mode. History mode cannot
        // change when traversing ajax link inside modal, it stays the same until modal is hidden.
        if (!this.modal.isShown()) {
            this.historyDirection = element.getAttribute('data-naja-modal-history') === 'forwards' ? 'forwards' : 'backwards';
        }
    }
    onSnippetFetch(event) {
        // When snippet cache is off, this method is called to construct Naja request options. We retrieve modal
        // `options` from original state in history if neccessary. Also, when closing the modal from `PopstateEvent`, we
        // don't need to make a new request, so we call `event.preventDefault()` in that case.
        const { state } = event.detail;
        if (this.isPdModalState(state)) {
            event.detail.options.pdModal = true;
            event.detail.options.modalOpener = this.getElementFromString(state.pdModal.opener);
            event.detail.options.modalOptions = state.pdModal.options;
        }
        if (this.shouldPreventSnippetFetch) {
            event.preventDefault();
            this.shouldPreventSnippetFetch = false;
        }
    }
    removeModalSnippetsIds() {
        // When closing the modal, we don't want to update any snippets inside it, when other requests finishes. This
        // will ensure, that snippets that might be also outside modal (e.g. flash messages) will be redrawn outside the
        // modal. Therefore, we remove the id attributes, so no snippet is found.
        //
        // Some snippets are necessary for modal function
        this.modal.element.querySelectorAll('[id^="snippet-"]').forEach((snippet) => {
            if (!this.modal.reservedSnippetIds.includes(snippet.id)) {
                snippet.removeAttribute('id');
            }
        });
    }
    abortPreviousRequest(event) {
        const { abortController, options } = event.detail;
        if (this.isPdModalRequest(options)) {
            this.abortControllers.get(this.uniqueExtKey)?.abort();
            this.abortControllers.set(this.uniqueExtKey, abortController);
        }
    }
    clearRequest(event) {
        const { request } = event.detail;
        if (!request.signal.aborted) {
            this.abortControllers.delete(this.uniqueExtKey);
        }
    }
    buildState(event) {
        const { options, state } = event.detail;
        // Every time naja builds the state, and we have the modal opened, we extend the state with `pdModal` object
        // containing information about modal being opened and what history mode is in use. When `options.forceRedirect`
        // is set, modal might be open but the new state will be redirected outside of it.
        //
        // We must check this.modal.isShown instead of this.isPdModalRequest, because of initial replace state from
        // Naja - in that case, options already contains information about request to be made, but we have to build the
        // "previous" state yet.
        const isShown = this.modal.isShown() && !options.forceRedirect;
        if (isShown) {
            state.pdModal = {
                historyDirection: this.historyDirection,
                opener: options.modalOpener.outerHTML,
                options: this.modalOptions
            };
        }
        // If the state is build, the history is enabled. This information is needed inside modal callback where the
        // options are not available, so we store this internally in extension.
        this.historyEnabled = true;
    }
    before(event) {
        const { options, request } = event.detail;
        if (!this.isPdModalRequest(options)) {
            return;
        }
        this.modal.show(options.modalOpener, options.modalOptions, event);
        request.headers.append('Pd-Modal-Opened', String(Number(this.modal.isShown())));
    }
    success(event) {
        const { options, payload } = event.detail;
        this.popstateFlag = false;
        this.lastState = {
            location: location.href,
            state: history.state,
            title: document.title
        };
        if (!this.isPdModalRequest(options)) {
            return;
        }
        const requestHistory = this.isRequestWithHistory(options);
        // If the history is disabled for current request, we will disable it for all ajax links / forms in modal as well.
        if (!requestHistory && event.target) {
            const ajaxified = this.modal.element.querySelectorAll(event.target.uiHandler?.selector);
            ajaxified.forEach((element) => {
                element.setAttribute('data-naja-history', 'off');
            });
        }
        if (payload.closeModal) {
            this.modal.hide(event);
        }
        else {
            this.modal.setOptions(this.modalOptions);
            if (this.modal.dispatchLoad) {
                this.modal.dispatchLoad(this.modalOptions, event);
            }
        }
    }
    showHandler() {
        this.modal.setOptions(this.modalOptions);
        // If the modal history mode is `forwards`, we store the state under the modal, so we can push it as a new state
        // after hiding the modal.
        if (this.historyDirection === 'forwards') {
            if (this.popstateFlag && this.lastState) {
                this.original.push(this.lastState);
            }
            else {
                const state = {
                    location: location.href,
                    state: history.state,
                    title: document.title
                };
                this.original.push(state);
            }
        }
    }
    hiddenHandler() {
        // This method is called after modal has been hidden. It either pushes a new state into history (mode `forwards`)
        // or calls `history.back()` to start go-back procedure.
        //
        // New state is pushed only if we are able to retrieve the state under the modal (which should have been stored
        // previously) and the modal is not being closed using forward / back buttons in browser.
        if (!this.historyEnabled) {
            return;
        }
        if (this.historyDirection === 'backwards') {
            // We don't know how many states we need to return. We go one by one, see popstate handler. This go-back
            // procedure is detected using `hidePopstateFlag`.
            this.hidePopstateFlag = true;
            this.cleanData();
            window.history.back();
        }
        else if (this.historyDirection === 'forwards') {
            const state = this.original.pop();
            this.original = [];
            if (state) {
                // When closing the modal using forward / back buttons in browser, the current state is the same as the
                // one stored in `this.original`. If that's the case, we don't push anything as it would duplicate the
                // state in history.
                if (history.state === undefined || history.state.href !== state.location) {
                    history.pushState(state.state, state.title, state.location);
                    document.title = state.title;
                    this.popstateFlag = false;
                    this.lastState = {
                        location: state.location,
                        state: state.state,
                        title: state.title
                    };
                }
            }
            this.cleanData();
        }
    }
    popstateHandler(event) {
        const state = event.state || this.initialState;
        if (typeof state === 'undefined' || !this.modal) {
            return;
        }
        const isCurrentStatePdModal = this.isPdModalState(state);
        this.popstateFlag = true;
        this.shouldPreventSnippetFetch = false;
        // We don't know how many states we go back. So we go one by one until the new state is not modal state
        // (`isPdModalState` is `false`).
        if (this.hidePopstateFlag) {
            // We don't want the naja popstate callback to be executed (or any other popstate handler).
            event.stopImmediatePropagation();
            if (isCurrentStatePdModal) {
                window.history.back();
                return;
            }
            else {
                // Todo check if this is really necessary. When used with nette.ajax / history.nette.ajax, this was necessary in some cases, where the title hasn't been restored correctly.
                if (state.title) {
                    document.title = state.title;
                }
            }
            this.hidePopstateFlag = false;
        }
        // We check if the state has `pdModal` object present on popstate. If so , we proceed to open the modal. Content
        // of the modal is restored by naja itself (either from cache or by new request).
        //
        // If the initial state is also detected as pdModal state, we returned to some pdModal state using reload. In
        // that case, we don't want to open the modal, because we might be missing some snippets. Effectively this means
        // that the modal will never be opened by forward / back button if there has been some other site loaded outside
        // the modal (e.g. some non-ajax link leading from modal).
        if (isCurrentStatePdModal && !this.isPdModalState(this.initialState)) {
            this.restoreExtensionPropertiesFromState(state);
            this.modal.show(this.getElementFromString(state.pdModal.opener), state.pdModal.options, event);
            // If there is some snippet cache, we might restore modal options. If not, options will be restored based on
            // options after the ajax request. Same applies to dispatching load event - if cache is on, we dispatch the
            // event immediately, otherwise it will be dispatched after ajax request.
            if (state.snippets?.storage !== 'off') {
                this.modal.setOptions(state.pdModal.options);
                if (this.modal.dispatchLoad) {
                    this.modal.dispatchLoad(this.modalOptions, event);
                }
            }
        }
        else {
            this.historyEnabled = false; // Hiding modal using forward / back button, we disable the history to prevent state duplication
            // Reload the page if the initial state has been inside modal. This prevents snippets loss e.g. during
            // layout changes.
            if (this.isPdModalState(this.initialState)) {
                window.location.reload();
            }
            // Non-modal state and non-modal initial state → we just hide the current modal and prevent snippet fetch
            // (if snippet cache is off).
            if (this.modal.isShown()) {
                this.modal.hide(event);
                this.shouldPreventSnippetFetch = true;
            }
        }
        // Keep track of current state. When `backwards` history mode is used, we eventually push this state into
        // `this.original`.
        this.lastState = {
            location: location.href,
            state: state,
            title: document.title
        };
    }
    cleanData() {
        this.historyEnabled = false;
        this.historyDirection = 'backwards';
        this.modalOptions = null;
    }
    getElementFromString(stringElement) {
        return new DOMParser().parseFromString(stringElement, 'text/html').body.firstElementChild;
    }
}

class SpinnerExtension {
    constructor(spinner, getSpinnerProps = undefined, ajaxSpinnerWrapSelector = '.ajax-wrap', ajaxSpinnerPlaceholderSelector = '.ajax-spinner') {
        this.spinner = spinner;
        this.getSpinnerProps = getSpinnerProps;
        this.ajaxSpinnerWrapSelector = ajaxSpinnerWrapSelector;
        this.ajaxSpinnerPlaceholderSelector = ajaxSpinnerPlaceholderSelector;
    }
    initialize(naja) {
        naja.uiHandler.addEventListener('interaction', this.getSpinnerInitiator.bind(this));
        naja.addEventListener('start', this.showSpinners.bind(this));
        naja.addEventListener('complete', this.hideSpinners.bind(this));
    }
    getSpinnerInitiator(event) {
        event.detail.options.spinnerInitiator = event.detail.element;
    }
    showSpinners(event) {
        const { options } = event.detail;
        if (!options.spinnerInitiator) {
            return;
        }
        const spinnerInitiator = options.spinnerInitiator;
        const placeholders = this.getPlaceholders(spinnerInitiator);
        if (placeholders.length === 0) {
            return;
        }
        else {
            options.spinnerQueue = options.spinnerQueue || [];
            placeholders.forEach((placeholder) => {
                let spinner;
                if (typeof this.spinner === 'function') {
                    spinner = this.getSpinnerProps ? this.spinner(this.getSpinnerProps(spinnerInitiator)) : this.spinner();
                }
                else {
                    spinner = this.spinner;
                }
                placeholder.appendChild(spinner);
                options.spinnerQueue.push(spinner);
                spinner.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 100 });
            });
        }
    }
    hideSpinners(event) {
        const { options } = event.detail;
        if (options.forceRedirect) {
            return;
        }
        options.spinnerQueue?.forEach((spinner) => {
            const animation = spinner.animate({ opacity: 0 }, { duration: 100 });
            animation.finished.then(() => spinner.remove());
        });
    }
    getPlaceholders(element) {
        if (!element) {
            return [];
        }
        const spinner = element.getAttribute('data-naja-spinner') || null;
        let placeholders = [];
        if (spinner === 'off') {
            return [];
        }
        placeholders = this.getPlaceholdersByQuerySelector(spinner);
        return placeholders.length ? placeholders : this.getPlaceholdersByDOM(element);
    }
    getPlaceholdersByQuerySelector(selector) {
        return selector ? Array.from(document.querySelectorAll(selector)) : [];
    }
    getPlaceholdersByDOM(element) {
        const wrap = element.closest(this.ajaxSpinnerWrapSelector);
        if (wrap === null) {
            return [];
        }
        const placeholders = wrap.querySelectorAll(this.ajaxSpinnerPlaceholderSelector);
        return placeholders.length ? Array.from(placeholders) : [wrap];
    }
}

const controlManager = new ControlManager();

export { AjaxModalExtension, SpinnerExtension, controlManager };
//# sourceMappingURL=PdNaja.esm.js.map
